import React, { ReactElement, useCallback, useContext, useMemo } from 'react';
import { TouchableOpacity } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { MBBottomSheet, MBBottomSheetTitleBar } from '@shared/components/bottomSheet';
import { MBIconV2Name } from '@shared/components/image';
import { ColorThemeContext, grid } from '@shared/styles';
import { useBoolean, useExecuteOnTrigger, useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { Route, useNavStack } from '@shared/util/navigation';
import { MBMultiSelect } from './MBMultiSelect';
import { MBMultiSelectEdit } from './edit';
import { MBMultiSelectModalProps } from './modalProps';

export const FIXED_WIDTH = { width: grid(46) };

const initialRoutes: Route[] = [{ name: 'select' }];

export function MBMultiSelectModal<T>({
  children,
  label,
  onOpen,
  onClose,
  itemsEditable = false,
  editMode,
  itemLabel,
  itemLabelPlural,
  onArchive,
  onUnarchive,
  onChangeLabel,
  trigger,
  anchorName,
  ...multiSelectProps
}: MBMultiSelectModalProps<T>): ReactElement {
  const isDesktop = useIsDesktop();
  const colorTheme = useContext(ColorThemeContext);
  const [visible, showModal, hideModal] = useBoolean(false);
  const nav = useNavStack(initialRoutes);

  const handleOpen = useCallback(() => {
    showModal();
    onOpen?.();
  }, [onOpen, showModal]);
  const handleClose = useCallback(() => {
    hideModal();
    nav.reset(initialRoutes);
    onClose?.();
  }, [onClose, nav, hideModal]);

  useExecuteOnTrigger(trigger, handleOpen);

  const title = useMemo(() => {
    if (nav.currentRoute?.name === 'manage') {
      return i18n('multiSelect.manage', { item: itemLabelPlural ?? '' });
    }
    if (nav.currentRoute?.name === 'edit') {
      return i18n('multiSelect.edit', { item: itemLabel ?? '' });
    }
    return label ?? '';
  }, [nav.currentRoute, itemLabelPlural, itemLabel, label]);

  const handleGearPress = useMemo(() => {
    if (nav.currentRoute?.name === 'select') {
      return () => nav.push({ name: 'manage' });
    }
    return undefined;
  }, [nav]);

  const handleBack = useMemo(() => {
    if (nav.currentRoute?.name === 'manage' || nav.currentRoute?.name === 'edit') {
      return () => nav.pop();
    }
    return undefined;
  }, [nav]);

  const handleEdit = useCallback(
    value => {
      nav.push({ name: 'edit', params: { value } });
    },
    [nav],
  );

  const handleArchive = useCallback(
    async (value: T) => {
      await onArchive?.(value);
      handleBack?.();
    },
    [handleBack, onArchive],
  );

  const handleUnarchive = useCallback(
    async (value: T) => {
      await onUnarchive?.(value);
      handleBack?.();
    },
    [handleBack, onUnarchive],
  );

  const handleChangeLabel = useCallback(
    async (updatedValue: T, updatedLabel: string) => {
      await onChangeLabel?.(updatedValue, updatedLabel);
      handleBack?.();
    },
    [handleBack, onChangeLabel],
  );
  const renderTitleBar = useCallback(() => {
    if (isDesktop && !itemsEditable) {
      return null;
    }

    if (isDesktop && itemsEditable) {
      return (
        <MBBottomSheetTitleBar
          title=""
          rightIcon="gear"
          rightIconColor={colorTheme.foregroundInactive}
          onRightIconPress={handleGearPress}
          rightIconSize={grid(4.5)}
          height={grid(6)}
          leftIcon={nav.currentRoute?.name !== 'select' ? 'arrow-point-left' : undefined}
          onLeftIconPress={handleBack}
          leftIconSize={grid(3.5)}
        />
      );
    }

    const exitIconProps = {
      rightIcon: 'exit' as MBIconV2Name,
      rightIconColor: colorTheme.foregroundActive,
      onRightIconPress: handleClose,
    };

    if (!itemsEditable) {
      return <MBBottomSheetTitleBar title={title} {...exitIconProps} />;
    }

    if (nav.currentRoute?.name === 'select') {
      return (
        <MBBottomSheetTitleBar
          title={title}
          leftIcon="gear"
          leftIconColor={colorTheme.foregroundInactive}
          leftTitlePosition={grid(8)}
          rightTitlePosition={grid(8)}
          onLeftIconPress={handleGearPress}
          {...exitIconProps}
        />
      );
    }
    return (
      <MBBottomSheetTitleBar
        title={title}
        leftIcon="arrow-point-left"
        onLeftIconPress={handleBack}
        leftTitlePosition={grid(8)}
        rightTitlePosition={grid(8)}
      />
    );
  }, [
    colorTheme.foregroundActive,
    colorTheme.foregroundInactive,
    handleBack,
    handleClose,
    handleGearPress,
    isDesktop,
    itemsEditable,
    nav,
    title,
  ]);

  const fixedWidth = useIsDesktop() ? FIXED_WIDTH : null;
  const archivedValues = multiSelectProps.archivedValues;
  const paramValue = nav.currentRoute?.params?.value as T;
  const isArchived = archivedValues?.includes(paramValue) ?? false;

  return (
    <>
      <TouchableOpacity onPress={handleOpen} style={{ maxWidth: '100%' }}>
        {children}
      </TouchableOpacity>
      {visible && (
        <MBBottomSheet
          anchorName={anchorName}
          onClose={handleClose}
          renderTitleBar={renderTitleBar}
        >
          {nav.currentRoute?.name === 'edit' ? (
            <MBView style={fixedWidth} fill>
              <MBMultiSelectEdit
                {...multiSelectProps}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
                onChangeLabel={handleChangeLabel}
                value={nav.currentRoute?.params?.value}
                isArchived={isArchived}
              />
            </MBView>
          ) : (
            <MBView style={fixedWidth} fill>
              <MBMultiSelect
                {...multiSelectProps}
                onClose={handleClose}
                editMode={nav.currentRoute?.name === 'manage'}
                onEdit={handleEdit}
              />
            </MBView>
          )}
        </MBBottomSheet>
      )}
    </>
  );
}
